import { useEffect, useState } from 'react';
import { useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import { frases } from './constants';
import { obtenerFotos } from '../../Services/FotoLlenado';
import { IUserFromStorage } from '../../Interfaces/IUser';
import { IObtenerFotosResponse, IFoto } from '../../Interfaces/FotoLlenado';
import ModalEvaluacion from '../../Components/ModalEvaluacion/ModalEvaluacion';
import LoadingAllScreen from '../../Components/LoadingAllScreen/LoadingAllScreen';
import ModalVideoProgreso from '../../Components/ModalVideoProgreso/ModalVideoProgreso';
import ModalLlenarPesosResultado from '../../Components/ModalLlenarPesosResultado/ModalLlenarPesosResultado';

import '../../Shared/css/TuProgreso/tuProgreso.css';

/* ── Photo Modal ── */
const PhotoModal = ({ foto, onClose }: { foto: string; onClose: () => void }) => (
  <div className="med-modal-overlay" onClick={onClose}>
    <button className="med-modal-close" onClick={onClose}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    </button>
    <img src={foto} alt="Foto de progreso" onClick={(e) => e.stopPropagation()} />
  </div>
);

/* ── Progress Card ── */
const ProgressCard = ({ fotoContainer, index }: { fotoContainer: IFoto; index: number }) => {
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const fotos = [fotoContainer.foto1, fotoContainer.foto2, fotoContainer.foto3, fotoContainer.foto4].filter(Boolean);
  const frase = frases[index] ? frases[index].frase : 'Sigue asi!!';

  return (
    <div className="med-progress-card">
      <div className="med-progress-header">
        <span className="med-progress-badge">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          Mes {index + 1}
        </span>
        <span className="med-progress-date">{fotoContainer.fechaRegistroString}</span>
      </div>

      <div className="med-progress-body">
        {/* Metrics */}
        <div className="med-progress-metrics">
          <div className="med-metric">
            <span className="med-metric-value">{fotoContainer.peso}<small style={{ fontSize: '0.65rem', fontWeight: 600 }}>{fotoContainer.unidadPeso}</small></span>
            <span className="med-metric-label">Peso</span>
          </div>
          <div className="med-metric">
            <span className="med-metric-value">{fotoContainer.porcentajeGrasa !== 0 ? `${fotoContainer.porcentajeGrasa}%` : '--'}</span>
            <span className="med-metric-label">Grasa</span>
          </div>
        </div>

        {/* Photo Gallery */}
        {fotos.length > 0 && (
          <div className="med-photos">
            {fotos.map((foto, i) => (
              <div key={i} className="med-photo-thumb" onClick={() => setSelectedPhoto(foto)}>
                <img src={foto} alt={`Progreso ${i + 1}`} />
              </div>
            ))}
          </div>
        )}

        {/* Motivational phrase */}
        <p className="med-progress-phrase" style={{ margin: '0.6rem 0 0', textAlign: 'center' }}>{frase}</p>
      </div>

      {selectedPhoto && (
        <PhotoModal foto={selectedPhoto} onClose={() => setSelectedPhoto(null)} />
      )}
    </div>
  );
};

/* ── Main Component ── */
function TuProgreso() {
  const [data, setData] = useState<IObtenerFotosResponse>();
  const [loading, setLoading] = useState(true);
  const [openModalLlenarFotos, setOpenModalLlenarFotos] = useState(false);
  const [isOpenModalVideoMedidas, setIsOpenModalVideoMedidas] = useState(false);
  const [isOpenModalEvaluacion, setIsOpenModalEvaluacion] = useState(false);

  const user: IUserFromStorage = JSON.parse(localStorage.getItem('user'));
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 650px)');

  useEffect(() => {
    window.scrollTo(0, 0);
    if (user.usuarioTestAsesoriaID !== 0) {
      obtenerFotos({ UsuarioTestAsesoriaID: user.usuarioTestAsesoriaID })
        .then((response) => setData(response.data.data))
        .catch(() => toast.error('No se pudo obtener las fotos, por favor, inténtalo de nuevo.'))
        .finally(() => setLoading(false));
    }
  }, []);

  useEffect(() => {
    if (data && data.mostrarControl) {
      setIsOpenModalEvaluacion(true);
    }
  }, [data]);

  // Compute summary stats from data
  const latestEntry = data?.fotos?.[data.fotos.length - 1];
  const firstEntry = data?.fotos?.[0];
  const totalEntries = data?.fotos?.length || 0;

  // Weight trend
  const getWeightTrend = () => {
    if (!data || data.fotos.length < 2) return null;
    const diff = latestEntry!.peso - firstEntry!.peso;
    if (diff === 0) return { direction: 'neutral' as const, value: '0' };
    return {
      direction: diff > 0 ? 'up' as const : 'down' as const,
      value: Math.abs(diff).toFixed(1),
    };
  };

  const weightTrend = getWeightTrend();

  return (
    <div className="med-page">
      {/* Header */}
      <div className="med-header" style={!isMobile ? { marginTop: '5vh'} : {}}>
        <button className="med-back-btn" onClick={() => navigate('/producto/2')}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <div>
          <span className="med-header-label">Tu progreso</span>
          <h1 className="med-header-title">Mi Evolución</h1>
        </div>
      </div>

      <div className="med-content">
        {loading ? (
          <div className="med-loading">
            <LoadingAllScreen />
          </div>
        ) : (
          <>
            {/* Video Banner */}
            <button className="med-video-banner" onClick={() => setIsOpenModalVideoMedidas(true)}>
              <div className="med-video-play-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#1A1A1A">
                  <polygon points="6,3 20,12 6,21" />
                </svg>
              </div>
              <div>
                <p className="med-video-banner-title">Mira este video primero</p>
                <p className="med-video-banner-sub">Te hará progresar más rápido</p>
              </div>
            </button>

            {/* Stats Summary */}
            {latestEntry && (
              <div className="med-stats-row">
                <div className="med-stat-card">
                  <div className="med-stat-icon med-stat-icon--weight">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                      <line x1="3" y1="6" x2="21" y2="6" />
                    </svg>
                  </div>
                  <span className="med-stat-value">
                    {latestEntry.peso}
                    {weightTrend && (
                      <span className={`med-trend med-trend--${weightTrend.direction}`}>
                        {weightTrend.direction === 'down' ? '↓' : weightTrend.direction === 'up' ? '↑' : '='}{weightTrend.value}
                      </span>
                    )}
                  </span>
                  <span className="med-stat-label">{latestEntry.unidadPeso} actual</span>
                </div>
                <div className="med-stat-card">
                  <div className="med-stat-icon med-stat-icon--fat">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                    </svg>
                  </div>
                  <span className="med-stat-value">{latestEntry.porcentajeGrasa !== 0 ? `${latestEntry.porcentajeGrasa}%` : '--'}</span>
                  <span className="med-stat-label">Grasa</span>
                </div>
                <div className="med-stat-card">
                  <div className="med-stat-icon med-stat-icon--entries">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                  </div>
                  <span className="med-stat-value">{totalEntries}</span>
                  <span className="med-stat-label">Registros</span>
                </div>
              </div>
            )}

            {/* Progress Timeline */}
            <h2 className="med-section-title">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#E8A817" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" />
              </svg>
              Historial de Progreso
            </h2>

            {data && data.fotos.length > 0 ? (
              data.fotos.map((fotoContainer, index) => (
                <ProgressCard
                  key={fotoContainer.retoTr4inerFotoID}
                  fotoContainer={fotoContainer}
                  index={index}
                />
              ))
            ) : (
              <div className="med-empty">
                <div className="med-empty-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" />
                  </svg>
                </div>
                <p className="med-empty-text">Aún no tienes registros de progreso.<br />¡Tu primera foto será tu punto de partida!</p>
              </div>
            )}
          </>
        )}
      </div>

      {openModalLlenarFotos && (
        <ModalLlenarPesosResultado isOpen={openModalLlenarFotos} setIsOpen={setOpenModalLlenarFotos} />
      )}
      {isOpenModalVideoMedidas && (
        <ModalVideoProgreso isOpen={isOpenModalVideoMedidas} setIsOpen={setIsOpenModalVideoMedidas} />
      )}
      {isOpenModalEvaluacion && (
        <ModalEvaluacion isOpen={isOpenModalEvaluacion} setIsOpen={setIsOpenModalEvaluacion} />
      )}
    </div>
  );
}

export default TuProgreso;
